/**
 * 비용 시뮬레이터 하단 설명 문단 + FAQ.
 *
 * 도구 페이지는 SSR 본문이 입력 라벨과 버튼 문구뿐이라 검색엔진이 읽을 내용이 없다.
 * 계산 전에도 항상 렌더해 SSR HTML 에 본문이 들어가게 한다.
 *
 * - 화면 문구와 FAQPage JSON-LD 를 `costSimulatorFaqs.ts` 한 배열에서 만든다.
 * - FAQ 는 <details> 로 접는다. 접혀 있어도 DOM 에 남아 크롤러가 읽고, JS 없이도 펼쳐진다.
 * - 구글 FAQ 리치 결과는 2026-05-07 종료됐다. 마크업은 해가 없어 유지하지만,
 *   효과는 화면에 보이는 본문에서 나온다 (structured-data-spec §5.2).
 *
 * 영구지침: docs/develop/domain/cost/비용_시뮬레이터_구현_지침.md §7.3
 */
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { FAQPageJsonLd } from '@/components/common/JsonLd'
import {
  COST_SIMULATOR_FAQS,
  COST_SIMULATOR_FAQ_TITLE,
  COST_SIMULATOR_INTRO,
  COST_SIMULATOR_INTRO_TITLE,
  toFaqJsonLdItems,
  type FaqSegment,
} from './costSimulatorFaqs'

const linkClass = 'font-medium text-cb-primary underline underline-offset-2'

function Segments({ segments }: { segments: readonly FaqSegment[] }) {
  return (
    <>
      {segments.map((s, i) =>
        typeof s === 'string' ? (
          <Fragment key={i}>{s}</Fragment>
        ) : (
          <Link key={i} to={s.to} className={linkClass}>
            {s.text}
          </Link>
        ),
      )}
    </>
  )
}

export default function CostSimulatorFaq() {
  return (
    <div className="flex flex-col gap-8">
      <FAQPageJsonLd faqs={toFaqJsonLdItems(COST_SIMULATOR_FAQS)} />

      <section aria-labelledby="cost-simulator-intro">
        <h2 id="cost-simulator-intro" className="mb-3 text-body font-bold text-cb-gray-900">
          {COST_SIMULATOR_INTRO_TITLE}
        </h2>
        <div className="flex flex-col gap-2.5 text-body-sm leading-relaxed text-cb-gray-600">
          {COST_SIMULATOR_INTRO.map((paragraph, i) => (
            <p key={i}>
              <Segments segments={paragraph} />
            </p>
          ))}
        </div>
      </section>

      <section aria-labelledby="cost-simulator-faq">
        <h2 id="cost-simulator-faq" className="mb-3 text-body font-bold text-cb-gray-900">
          {COST_SIMULATOR_FAQ_TITLE}
        </h2>
        <div className="divide-y divide-cb-gray-200 overflow-hidden rounded-xl border border-cb-gray-200 bg-white">
          {COST_SIMULATOR_FAQS.map((faq) => (
            <details key={faq.question} className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 text-left [&::-webkit-details-marker]:hidden">
                <h3 className="text-body-sm font-medium text-cb-gray-800">{faq.question}</h3>
                <ChevronDown
                  size={18}
                  aria-hidden="true"
                  className="flex-shrink-0 text-cb-gray-400 transition-transform group-open:rotate-180"
                />
              </summary>
              <p className="px-4 pb-4 text-body-sm leading-relaxed text-cb-gray-600">
                <Segments segments={faq.segments} />
              </p>
            </details>
          ))}
        </div>
      </section>
    </div>
  )
}
